import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Upload, FileText, Loader2, AlertCircle, CheckCircle2, ArrowRight, Plus, ChevronDown } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ManualPaymentDialog } from '@/components/accounting/ManualPaymentDialog';
import { accountingAPI, carrierAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { isKRRole, formatDate } from '@/lib/utils';
import { toast } from 'sonner';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const STATUS_COLORS = {
  imported: 'bg-slate-100 text-slate-700',
  mapping:  'bg-amber-100 text-amber-800',
  ready:    'bg-emerald-100 text-emerald-800',
  archived: 'bg-slate-200 text-slate-500',
};

function canAccessAccounting(user) {
  if (isKRRole(user)) return true;
  const roles = (user?.roles || []).map(r => r.role);
  return roles.includes('Accounting') || user?.active_context?.role === 'Accounting';
}

export default function CarrierImportsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [showManualPayment, setShowManualPayment] = useState(false);
  const [carriers, setCarriers] = useState([]);
  const [form, setForm] = useState({
    carrier_id: '',
    period_year: new Date().getFullYear(),
    period_month: new Date().getMonth() + 1,
    period_day: new Date().getDate(),
    notes: '',
    file: null,
  });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!canAccessAccounting(user)) return;
    load();
    carrierAPI.list().then((r) => setCarriers(r.data || [])).catch(() => {});
  }, [user]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await accountingAPI.listImports({ page_size: 100 });
      setItems(data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load imports');
    } finally { setLoading(false); }
  };

  const submitUpload = async () => {
    if (!form.carrier_id || !form.file) {
      toast.error('Carrier and file are required');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('carrier_id', form.carrier_id);
      fd.append('period_year', String(form.period_year));
      fd.append('period_month', String(form.period_month));
      fd.append('period_day', String(form.period_day));
      if (form.notes) fd.append('notes', form.notes);
      fd.append('file', form.file);
      const { data } = await accountingAPI.createImport(fd);
      toast.success(`Imported — ${data.line_count} lines parsed`);
      setShowUpload(false);
      setForm({ ...form, file: null, notes: '' });
      await load();
      navigate(`/accounting/carrier-imports/${data.import_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  if (!canAccessAccounting(user)) {
    return <div className="p-8 text-center text-slate-500">Accounting or KR admins only.</div>;
  }

  return (
    <div className="page-content">
      <PageHeader
        title="Payments"
        description="All incoming carrier commission statements plus any manual payments (bonuses, reimbursements, corrections)."
        icon={<FileText size={20} />}
        actions={
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button data-testid="add-payment-btn">
                <Plus size={14} className="mr-1" /> Add Payment
                <ChevronDown size={13} className="ml-1 opacity-70" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setShowUpload(true)} data-testid="add-payment-import">
                <Upload size={13} className="mr-2" /> Import Statement
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setShowManualPayment(true)} data-testid="add-payment-manual">
                <Plus size={13} className="mr-2" /> Add Manually
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        }
      />

      {loading ? (
        <div className="py-16 text-center text-slate-400">
          <Loader2 className="mx-auto animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <Card className="p-10 text-center text-slate-500" data-testid="empty-imports">
          <FileText className="mx-auto mb-2 text-slate-400" size={28} />
          <div>No carrier statements yet.</div>
          <Button className="mt-4" onClick={() => setShowUpload(true)}>Upload your first statement</Button>
        </Card>
      ) : (
        <Card data-testid="carrier-imports-list">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Recent Imports</CardTitle>
          </CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-slate-500 border-b">
                  <th className="py-2">Period</th>
                  <th className="py-2">Carrier</th>
                  <th className="py-2">File</th>
                  <th className="py-2">Lines</th>
                  <th className="py-2">Needs Review</th>
                  <th className="py-2">Status</th>
                  <th className="py-2">Uploaded</th>
                  <th className="py-2"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr
                    key={it.import_id}
                    className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                    onClick={() => navigate(`/accounting/carrier-imports/${it.import_id}`)}
                    data-testid={`import-row-${it.import_id}`}
                  >
                    <td className="py-2 font-medium">{it.period_label}</td>
                    <td className="py-2">
                      {it.statement_source === 'manual' && !it.carrier_id ? (
                        <Badge variant="outline" className="text-[10px]" data-testid={`carrier-na-${it.import_id}`}>N/A</Badge>
                      ) : (
                        it.carrier_name
                      )}
                    </td>
                    <td className="py-2 truncate max-w-[200px] text-xs">
                      {it.statement_source === 'manual' ? (
                        <Badge variant="secondary" className="text-[10px] bg-violet-100 text-violet-700 hover:bg-violet-200" data-testid={`manual-badge-${it.import_id}`}>
                          Manual · {it.payment_type ? it.payment_type.charAt(0).toUpperCase() + it.payment_type.slice(1) : 'Payment'}
                        </Badge>
                      ) : (
                        it.file_original_filename
                      )}
                    </td>
                    <td className="py-2 font-mono text-xs">{it.line_count}</td>
                    <td className="py-2">
                      {it.lines_with_missing_required > 0 ? (
                        <Badge className="bg-amber-100 text-amber-800 gap-1">
                          <AlertCircle size={10} /> {it.lines_with_missing_required}
                        </Badge>
                      ) : (
                        <Badge className="bg-emerald-100 text-emerald-800 gap-1">
                          <CheckCircle2 size={10} /> 0
                        </Badge>
                      )}
                    </td>
                    <td className="py-2">
                      <Badge className={STATUS_COLORS[it.status] || 'bg-slate-100'}>
                        {it.status}
                      </Badge>
                    </td>
                    <td className="py-2 text-xs text-slate-500">{formatDate(it.uploaded_at)}</td>
                    <td className="py-2"><ArrowRight size={14} className="text-slate-400" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Upload dialog */}
      <Dialog open={showUpload} onOpenChange={(v) => { if (!uploading) setShowUpload(v); }}>
        <DialogContent data-testid="upload-import-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Upload Carrier Statement</DialogTitle>
            <DialogDescription>
              XLSX and CSV files are auto-parsed. PDFs are stored for manual mapping.
              50 MB max per file.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <Label>Carrier *</Label>
              <Select value={form.carrier_id} onValueChange={(v) => setForm({ ...form, carrier_id: v })}>
                <SelectTrigger data-testid="upload-carrier-select"><SelectValue placeholder="Select carrier" /></SelectTrigger>
                <SelectContent>
                  {carriers.map((c) => (
                    <SelectItem key={c.carrier_id} value={c.carrier_id}>{c.carrier_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label>Month</Label>
                <Select value={String(form.period_month)} onValueChange={(v) => setForm({ ...form, period_month: parseInt(v) })}>
                  <SelectTrigger data-testid="upload-month"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MONTHS.map((m, i) => (
                      <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Day</Label>
                <Input type="number" min={1} max={31} value={form.period_day}
                       onChange={(e) => setForm({ ...form, period_day: parseInt(e.target.value || '1') })}
                       data-testid="upload-day" />
              </div>
              <div>
                <Label>Year</Label>
                <Input type="number" min={2020} max={2100} value={form.period_year}
                       onChange={(e) => setForm({ ...form, period_year: parseInt(e.target.value || '2026') })}
                       data-testid="upload-year" />
              </div>
            </div>
            <div>
              <Label>File * (XLSX, CSV, PDF — max 50 MB)</Label>
              <input
                type="file"
                accept=".xlsx,.xls,.xlsm,.csv,.pdf"
                onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })}
                data-testid="upload-file-input"
                className="block w-full text-sm text-slate-700 file:mr-3 file:py-1.5 file:px-3
                           file:rounded-md file:border-0 file:text-sm file:font-medium
                           file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
              />
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea rows={2} value={form.notes}
                        onChange={(e) => setForm({ ...form, notes: e.target.value })}
                        data-testid="upload-notes" />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUpload(false)} disabled={uploading}>Cancel</Button>
            <Button onClick={submitUpload} disabled={uploading || !form.carrier_id || !form.file}
                    data-testid="upload-submit-btn">
              {uploading ? 'Uploading…' : 'Upload'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 27.10 — Manual Payment dialog (Fase A: UI-only skeleton). */}
      <ManualPaymentDialog
        open={showManualPayment}
        onOpenChange={setShowManualPayment}
        onSaved={load}
      />
    </div>
  );
}
